import { useEffect, useState } from "react";
import classNames from "classnames";
import { useResponsive } from "@outplayed/responsive";
import { useAdFree } from "lib/hooks/use-ad-free";
import { FreestarSlot } from "components/Ads/FreestarInContent";
import { FreestarPlacements } from "components/Ads/freestar-placements";

const DESKTOP_BREAKPOINTS = ["DESKTOP_SMALL", "DESKTOP_MEDIUM", "DESKTOP_LARGE"];
const RAIL_WIDTHS = [300, 160] as const;
type RailWidth = (typeof RAIL_WIDTHS)[number];
type RailSide = "left" | "right";

const RAIL_PLACEMENTS: Record<RailSide, Record<RailWidth, FreestarPlacements[]>> = {
  left: {
    300: [FreestarPlacements.MedRectATF, FreestarPlacements.MedRectBTF],
    160: [FreestarPlacements.LeftRail],
  },
  right: {
    300: [FreestarPlacements.MedRectBTF2],
    160: [], //requested not to run u-gg_right_rail in narrow rail widths
  },
};

// Callback ref so measuring starts whenever the probe mounts (the rail renders null while ad-free state loads).
function useRailWidth() {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<RailWidth | null>(null);

  useEffect(() => {
    if (!el) return;

    const measure = () => {
      const gutter = el.getBoundingClientRect().width;
      setWidth(RAIL_WIDTHS.find((w) => w <= gutter) ?? null);
    };

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    measure();
    return () => observer.disconnect();
  }, [el]);

  return { probe: setEl, width };
}

interface FreestarRailProps {
  side: RailSide;
  className?: string;
}

function FreestarRail({ side, className }: FreestarRailProps) {
  const isAdFree = useAdFree();
  const { currentBreakpoint } = useResponsive();
  const { probe, width } = useRailWidth();
  const isDesktop = !!currentBreakpoint && DESKTOP_BREAKPOINTS.includes(currentBreakpoint);

  if (isAdFree || !isDesktop) return null;

  return (
    <div id={`ugg-${side}-rail`} className={classNames("flex flex-col items-center", className)}>
      <div ref={probe} className="w-full" />
      {width !== null && (
        // Keyed on width so a size change re-requests a creative that fits.
        <div key={width} className="flex flex-col gap-[12px] overflow-hidden" style={{ width }}>
          {RAIL_PLACEMENTS[side][width].map((placement, index) => (
            <FreestarSlot key={placement} placement={placement} slotId={`${side}-rail-${index + 1}`} />
          ))}
        </div>
      )}
    </div>
  );
}

interface RailProps {
  className?: string;
}

export const FreestarLeftRail = ({ className }: RailProps) => <FreestarRail side="left" className={className} />;
export const FreestarRightRail = ({ className }: RailProps) => <FreestarRail side="right" className={className} />;
